import { CloseButton, Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import { ChevronRightIcon, PaperClipIcon } from "@heroicons/react/24/outline";
import { Check, MatchChecksDashboard } from "../../../../../components/matches/MatchChecks.tsx";
import { Tag } from "../../../../../components/general/Tag.tsx";
import { cleanDocumentFileName } from "../../../../../lib/TFICLib.ts";
import { MdrRejection, MdrRejectionCriteria } from "./MissingDocumentRequest.ts";
import type { DraftMatchResolutionReason } from "../../../../../generated/enums.ts";

const rejectionSentences: Record<DraftMatchResolutionReason, string | null> = {
    scoring_no_match: "Dit document lijkt niet te horen bij deze transactie, probeer een ander document",
    previously_rejected_combination: "Deze combinatie is eerder ontkoppeld door je boekhouder.",
    document_not_a_bill: "Dit document is geen factuur of bon. Upload de factuur of bon die bij deze transactie hoort.",
    document_processing_failed: "We konden dit document niet verwerken. Upload een duidelijke PDF of foto van de factuur of bon.",
    bill_not_found: null,
    cancelled_by_entrepreneur: null,
    transaction_already_matched: null,
    bill_already_matched: null,
    system_already_matched: null,
};

function sentenceFor(rejection: MdrRejection): string | null {
    if (rejection.reason === 'scoring_no_match' && !rejection.criteria) return null;
    return rejectionSentences[rejection.reason] ?? null;
}

function toChecks(criteria: MdrRejectionCriteria): Check[] {
    const checks: Check[] = [
        {
            text: criteria.amount_matches ? "Bedrag komt overeen" : "Bedrag komt niet overeen",
            status: criteria.amount_matches ? "ok" : "error",
        },
        {
            text: criteria.date_is_close ? "Datum ligt dicht bij de transactie" : "Datum ligt te ver van de transactie",
            status: criteria.date_is_close ? "ok" : "error",
        },
    ];

    if (criteria.relation_matches !== null) {
        checks.push({
            text: criteria.relation_matches ? "Relatie komt overeen" : "Relatie komt niet overeen",
            status: criteria.relation_matches ? "ok" : "error",
        });
    }

    if (criteria.invoice_number_matches !== null) {
        checks.push({
            text: criteria.invoice_number_matches
                ? "Factuurnummer in transactieomschrijving"
                : "Factuurnummer niet in transactieomschrijving",
            status: criteria.invoice_number_matches ? "ok" : "error",
        });
    }

    return checks;
}

function AttachedDocument({ filename, onOpen }: { filename: string; onOpen?: () => void }) {
    const documentFilename = cleanDocumentFileName(filename);
    const content = (
        <>
            <PaperClipIcon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            {documentFilename}
        </>
    );

    if (!onOpen) {
        return (
            <span className="inline-flex items-center gap-1.5 text-xs text-gray-500" title={documentFilename}>
                {content}
            </span>
        );
    }

    return (
        <CloseButton
            onClick={(event) => {
                event.stopPropagation();
                onOpen();
            }}
            className="inline-flex items-center gap-1.5 self-start rounded text-left text-xs text-blue-600 hover:underline focus:outline-none data-[focus]:ring-2 data-[focus]:ring-blue-300"
            title={documentFilename}
            aria-label={`${documentFilename} bekijken`}
        >
            {content}
        </CloseButton>
    );
}

interface MdrRejectionReasonProps {
    rejection: MdrRejection;
    onOpenDocument?: () => void;
}

export function MdrRejectionReason({ rejection, onOpenDocument }: MdrRejectionReasonProps) {
    const sentence = sentenceFor(rejection);
    if (sentence === null) return null;

    return (
        <Popover className="mt-1.5">
            <PopoverButton onClick={(event) => event.stopPropagation()} className="rounded-full focus:outline-none data-[focus]:ring-2 data-[focus]:ring-red-300">
                <Tag variant="error" size="xs" className="hover:bg-red-200">
                    Match mislukt
                    <ChevronRightIcon className="h-3 w-3" aria-hidden="true" />
                </Tag>
            </PopoverButton>
            <PopoverPanel
                anchor={{ to: "right start", gap: 8 }}
                className="z-[60] flex w-72 flex-col gap-2 rounded-xl border border-gray-200 bg-white p-3 shadow-lg ring-1 ring-black/5 focus:outline-none"
            >
                <p className="text-sm text-gray-700">{sentence}</p>
                {rejection.criteria && (
                    <div className="border-t border-gray-100 pt-2">
                        <MatchChecksDashboard checks={toChecks(rejection.criteria)} />
                    </div>
                )}
                <AttachedDocument filename={rejection.document_filename} onOpen={onOpenDocument} />
            </PopoverPanel>
        </Popover>
    );
}
